import { useTranslation } from 'react-i18next';
import { Check, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { evaluateRequirements, type PasswordPolicy } from '../lib/passwordPolicy';

import type { JSX } from "react";

interface PasswordRequirementsProps {
  /** Current password value. Rules re-evaluate live on every keystroke. */
  password: string;
  /** Policy from `useGetPasswordPolicyQuery`. Undefined while it loads. */
  policy?: PasswordPolicy;
  /** Show the "can't reuse last 5 passwords" informational note (static copy
   *  — the user-service enforces no password history, so this is UI-only and
   *  matches the legacy reset form). */
  showInfo?: boolean;
}

/**
 * Live password-requirements checklist, shared by the onboard and
 * reset-password forms. Recomputes each rule's pass/fail against the typed
 * password on every render (it does NOT read react-hook-form validity), using
 * the single `evaluateRequirements` evaluator so the live feedback matches the
 * submit-time `buildPasswordSchema` validation exactly. Labels come from i18n
 * (`passwordRules.*`, namespace `auth`).
 *
 * We render only rules whose requirement is positive. The live policy sets
 * every field > 0 so the output matches the legacy six-row list; this guard
 * just drops always-satisfied "0 of X" noise if a policy ever ships a zero.
 */
export function PasswordRequirements({
  password,
  policy,
  showInfo = false,
}: PasswordRequirementsProps): JSX.Element | null {
  const { t } = useTranslation('auth');

  const rules = policy
    ? evaluateRequirements(password, policy).filter((rule) => (policy[rule.id] ?? 0) > 0)
    : [];

  if (rules.length === 0 && !showInfo) return null;

  return (
    <ul className="flex flex-col gap-1 text-xs" data-testid="password-requirements">
      {rules.map((rule) => (
        <li
          key={rule.id}
          className={cn(
            'flex items-center gap-1.5',
            rule.isValid ? 'text-emerald-600 dark:text-emerald-500' : 'text-muted-foreground',
          )}
          data-testid={`password-requirements-rule-${rule.id}`}
          data-valid={rule.isValid}
        >
          {rule.isValid ? (
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )}
          <span>{t(`passwordRules.${rule.id}`, { count: policy?.[rule.id] })}</span>
        </li>
      ))}
      {showInfo && (
        <li
          className="flex items-center gap-1.5 italic text-muted-foreground"
          data-testid="password-requirements-reuse-info"
        >
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{t('passwordRules.reuseInfo')}</span>
        </li>
      )}
    </ul>
  );
}
